import { JSONUtils } from '../../utility/json-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Cardinal } from '../core/cardinal.js';
import {
  cloneData, cloneList, isSameData, isSameList,
} from '../dto/data-model-utils.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { ITicketPool } from './ticketing-state/ticket-pool.js';

export class TicketCapacityGroup extends DataModel {
  static readonly TYPE_ID = '6378771d0b404d7c920fd2595a258e6a';

  private constructor(
    public readonly uniqueId: string,
    public readonly ticketTemplateIds: string[],
    public readonly capacity: Cardinal,
  ) {
    super();
  }

  get $typeId(): string {
    return TicketCapacityGroup.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    ticketTemplateIds: string[],
    capacity: Cardinal,
  }): TicketCapacityGroup {
    return new TicketCapacityGroup(
        args.uniqueId,
        args.ticketTemplateIds,
        args.capacity,
    );
  }

  static fromMap(_context: SerializationContext, map: SerializedData): TicketCapacityGroup {
    return TicketCapacityGroup.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      ticketTemplateIds: MapUtils.getList(map, 'ticketTemplateIds', (value) => JSONUtils.decodeString(value)),
      capacity: Cardinal.decode(MapUtils.getInteger(map, 'capacity')),
    });
  }

  toMap(_context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'ticketTemplateIds': this.ticketTemplateIds,
      'capacity': this.capacity.encode(),
    };
  }

  getRemainingCapacity(ticketPool: ITicketPool): Cardinal {
    if(this.capacity.isInfinite) return Cardinal.infinity;

    const usedCapacity = this.ticketTemplateIds.reduce(
        (total, ticketTemplateId) => total + ticketPool.getTicketCount(ticketTemplateId),
        0,
    );
    return Cardinal.finite(Math.max(this.capacity.value - usedCapacity, 0));
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    ticketTemplateIds = cloneList(this.ticketTemplateIds),
    capacity = cloneData(this.capacity),
  }: {
    uniqueId?: string,
    ticketTemplateIds?: string[],
    capacity?: Cardinal,
  } = {}): this {
    return TicketCapacityGroup.create({
      uniqueId,
      ticketTemplateIds,
      capacity,
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      isSameList(other.ticketTemplateIds, this.ticketTemplateIds) &&
      isSameData(other.capacity, this.capacity);
  }
}

export class TicketCapacityGroupBuilder extends DataModelBuilder<TicketCapacityGroup> {
  build(context: SerializationContext, map: SerializedData): TicketCapacityGroup {
    return TicketCapacityGroup.fromMap(context, map);
  }
}
